import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { friendlyError } from '../utils/friendlyError';
import './Login.css';
import { AuthShell } from './auth/AuthShell';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, MailCheck } from 'lucide-react';
import { Rise, SPRING_FAST } from '../components/ui/motion';
import { StatePanel, ErrorCard } from '../components/ui/StatePanel';
import { Input, Label } from '../components/ui/input';
import { PasswordInput } from '../components/ui/PasswordInput';
import { isOnboardingComplete, leadsTeam } from '../utils/onboarding';
import { Button } from '../components/ui/button';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { toast } from 'react-toastify';
import { useGoogleSignInAvailable, useGoogleButtonWidth } from '../utils/googleAuth';

const Login: React.FC = () => {
  const location = useLocation();
  // A personal email link (pages/EmailLinkLanding) fills the address in, so
  // the recipient only types their password, and says where to go after.
  const linkState = (location.state || {}) as { prefillEmail?: unknown; from?: unknown };
  const [formData, setFormData] = useState({
    email: typeof linkState.prefillEmail === 'string' ? linkState.prefillEmail : '',
    password: '',
  });
  const [error, setError] = useState('');
  const googleAvailable = useGoogleSignInAvailable();
  const [googleBtnRef, googleBtnWidth] = useGoogleButtonWidth();
  const [loading, setLoading] = useState(false);

  // Passwordless sign-in. 'password' is the default; 'otp-email' asks where to
  // send the code; 'otp-code' collects it. The email lives in formData either
  // way, so switching modes never makes the user retype it.
  const [mode, setMode] = useState<'password' | 'otp-email' | 'otp-code'>('password');
  const [otpCode, setOtpCode] = useState('');
  const [otpNotice, setOtpNotice] = useState('');
  const [resendIn, setResendIn] = useState(0);

  const { login, googleLogin, loginWithOtp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Show success toast when redirected after email verification
  useEffect(() => {
    if (searchParams.get('verified') === 'true') {
      toast.success('Email verified successfully! You can now log in.');
    }
  }, [searchParams]);

  // Counts the resend cooldown down to zero. Mirrors the backend's 90s window;
  // the server enforces it regardless, this only stops people mashing the link
  // and being told "no" each time.
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  /** Both sign-in paths land in the same place. */
  const goAfterLogin = (response: any) => {
    // A manager goes to their team Home rather than the learner diagnostic (MQ1).
    const needsOnboarding =
      !isOnboardingComplete(response?.user?.onboarding) && !leadsTeam(response?.user);
    // Back to the page they were sent to (a bounce, or an email link) when
    // there is one. Only an in-app path, never a full URL.
    const from =
      typeof linkState.from === 'string' &&
      linkState.from.startsWith('/') &&
      !linkState.from.startsWith('//')
        ? linkState.from
        : '/';
    navigate(needsOnboarding ? '/baseline-quiz' : from, { replace: true });
  };

  /** Shared failure handling. The verification/approval gate can fire on either
   *  path, so both need to route to /verify-email rather than show a dead-end
   *  error. Everything else falls through to `fallback`. */
  const handleLoginError = (err: any, fallback: string) => {
    if (err.response?.status === 403 && err.response?.data?.requiresVerification) {
      const email = err.response?.data?.email || formData.email;
      // Carry the gate's reason through. Without it every block reads
      // "check your inbox", which is actively misleading on an environment
      // where an administrator - not the email - is what stands in the way.
      const reason = err.response?.data?.awaitingApproval ? err.response?.data?.reason : '';
      navigate(
        `/verify-email?email=${encodeURIComponent(email)}` +
          (reason ? `&reason=${encodeURIComponent(reason)}` : ''),
      );
      return;
    }
    setError(err.response?.data?.message || fallback);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response: any = await login(formData.email, formData.password);
      goAfterLogin(response);
    } catch (err: any) {
      handleLoginError(err, 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  /** Request a code. The reply is deliberately identical whether or not the
   *  address has an account, so we always advance to the code step - stopping
   *  short for unknown addresses would turn this screen into a way of finding
   *  out who is registered. */
  const sendOtp = async (isResend = false) => {
    setLoading(true);
    setError('');
    try {
      await authAPI.requestLoginOtp(formData.email.trim());
      setOtpNotice(
        isResend
          ? 'If that email has an account, a new code is on its way.'
          : 'If that email has an account, a sign-in code is on its way.',
      );
      setMode('otp-code');
      setResendIn(90);
      setOtpCode('');
    } catch (err: any) {
      // A 503 means no template is configured, i.e. the feature is switched
      // off. That is a real, actionable message rather than an enumeration
      // risk, so the server's wording is shown as-is.
      setError(err.response?.data?.message || 'Could not send a code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendOtp(false);
  };

  const handleOtpCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setOtpNotice('');
    try {
      const response: any = await loginWithOtp(formData.email.trim(), otpCode.trim());
      goAfterLogin(response);
    } catch (err: any) {
      handleLoginError(err, 'Invalid or expired code');
      setOtpCode('');
    } finally {
      setLoading(false);
    }
  };

  /** Leaving a mode should not leave a stale code, notice or error behind. */
  const switchMode = (next: 'password' | 'otp-email' | 'otp-code') => {
    setMode(next);
    setError('');
    setOtpNotice('');
    if (next !== 'otp-code') setOtpCode('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      setError('Google login failed. Please try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await googleLogin(
        credentialResponse.credential,
        undefined,
        undefined,
        undefined,
        { mode: 'login' },
      );
      const needsOnboarding =
        response?.isNewUser ||
        (!isOnboardingComplete(response?.user?.onboarding) && !leadsTeam(response?.user));
      navigate(needsOnboarding ? '/baseline-quiz' : '/');
    } catch (err: any) {
      if (err.response?.data?.requiresCompany) {
        navigate('/register', { state: { googleCredential: credentialResponse.credential } });
      } else {
        setError(friendlyError(err, 'Google login failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google login failed. Please try again.');
  };

  const subtitle = (
    <>
      {mode === 'password' &&
        'Pick up where you left off - one short practice can shift your week.'}
      {mode === 'otp-email' &&
        "Enter your email and we'll send you a 6-digit sign-in code. No password needed."}
      {mode === 'otp-code' && (
        <>
          Enter the 6-digit code we sent to <strong>{formData.email}</strong>. It expires in 10
          minutes.
        </>
      )}
    </>
  );

  return (
    <AuthShell title="Welcome back" subtitle={subtitle}>
      {/* Mode switches (password / otp-email / otp-code) cross-fade on
          SPRING_FAST instead of hard-swapping; each form keeps its own
          handlers and copy. */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={mode}
          className="login-mode"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4, transition: { duration: 0.12 } }}
          transition={SPRING_FAST}
        >
          {mode === 'password' && (
            <form onSubmit={handleSubmit} className="login-form">
              {error && <ErrorCard layout="inline" description={error} className="error-message" />}

              <Rise as="div" index={2} className="form-group">
                <Label htmlFor="email">Email</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </Rise>

              <Rise as="div" index={3} className="form-group">
                <Label htmlFor="password">Password</Label>
                <PasswordInput
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <div className="forgot-password-link">
                  <Link to="/forgot-password">Forgot password?</Link>
                </div>
              </Rise>

              <Rise as="div" index={4}>
                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  className="login-btn"
                  disabled={loading}
                >
                  {loading ? 'Signing in…' : 'Log in'}
                </Button>
              </Rise>

              <Rise as="div" index={5} className="login-alt">
                {/* Divider and button share one condition — see useGoogleSignInAvailable.
              Rendering the divider unconditionally left it separating the form
              from empty space whenever Google's script failed to load. */}
                {googleAvailable && (
                  <>
                    <div className="divider">
                      <span>or</span>
                    </div>

                    {/* The button waits for the wrapper to be measured — mounting it
                  with a placeholder width and correcting it afterwards
                  re-initialises GSI. See useGoogleButtonWidth. */}
                    <div className="google-btn-wrapper" ref={googleBtnRef}>
                      {googleBtnWidth && (
                        <GoogleLogin
                          onSuccess={handleGoogleSuccess}
                          onError={handleGoogleError}
                          theme="outline"
                          size="large"
                          width={googleBtnWidth}
                          text="signin_with"
                        />
                      )}
                    </div>
                  </>
                )}

                {/* A second way in, so it gets the same weight as the Google button
              rather than reading as a stray hyperlink under it. The divider
              above already says "or", which covers both alternatives. */}
                {/* Width tracks the Google button's measured width so the two line up
              exactly; GSI sizes itself, so a fixed max-width alone drifts. */}
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="alt-signin-btn"
                  style={
                    googleAvailable && googleBtnWidth ? { maxWidth: googleBtnWidth } : undefined
                  }
                  onClick={() => switchMode('otp-email')}
                >
                  <Mail className="lnr-icon" aria-hidden="true" />
                  Email me a sign-in code
                </Button>
              </Rise>
            </form>
          )}

          {mode === 'otp-email' && (
            <form onSubmit={handleOtpEmailSubmit} className="login-form">
              {error && <ErrorCard layout="inline" description={error} className="error-message" />}

              <div className="form-group">
                <Label htmlFor="otp-email">Email</Label>
                <Input
                  type="email"
                  id="otp-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <Button
                type="submit"
                variant="default"
                size="lg"
                className="login-btn"
                disabled={loading}
              >
                {loading ? 'Sending…' : 'Send me a code'}
              </Button>

              <div className="alt-signin-link">
                <button
                  type="button"
                  className="link-button"
                  onClick={() => switchMode('password')}
                >
                  Use my password instead
                </button>
              </div>
            </form>
          )}

          {mode === 'otp-code' && (
            <form onSubmit={handleOtpCodeSubmit} className="login-form">
              {error && <ErrorCard layout="inline" description={error} className="error-message" />}
              {otpNotice && !error && (
                <StatePanel
                  layout="inline"
                  icon={<MailCheck className="lnr-icon" aria-hidden="true" />}
                  description={otpNotice}
                  className="notice-message"
                  role="status"
                />
              )}

              <div className="form-group">
                <Label htmlFor="otp-code">6-digit code</Label>
                {/* inputMode/pattern get the numeric keypad on mobile;
                autoComplete="one-time-code" lets iOS and Android offer the
                code straight from the notification. */}
                <Input
                  type="text"
                  id="otp-code"
                  name="code"
                  className="otp-code-input"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  placeholder="000000"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                />
              </div>

              <Button
                type="submit"
                variant="default"
                size="lg"
                className="login-btn"
                disabled={loading || otpCode.length !== 6}
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </Button>

              <div className="alt-signin-link">
                <button
                  type="button"
                  className="link-button"
                  onClick={() => sendOtp(true)}
                  disabled={loading || resendIn > 0}
                >
                  {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
                </button>
                <span className="alt-signin-sep">·</span>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => switchMode('otp-email')}
                >
                  Use a different email
                </button>
              </div>

              <div className="alt-signin-link">
                <button
                  type="button"
                  className="link-button"
                  onClick={() => switchMode('password')}
                >
                  Use my password instead
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </AnimatePresence>

      {/* The contextual "Forgot password?" already sits directly under the
          password field, which is where it is useful. A second identical
          link ~180px below it in the footer added nothing and read as a
          duplicate — especially with the Google button between them. */}
    </AuthShell>
  );
};

export default Login;
